import React from 'react';

// ui-language.js — the design-language toggle (owner, 2026-08-03).
//
// Two languages share one app:
//   'classic' — the shipped Calm-Clinical shadcn chrome (top-bar nav). Default.
//   'v2'      — the declutter-tiers language (left icon rail, top capture dock,
//               healther.tech palette via styles/ui-v2.css, tiered screens).
//
// The choice is device-local (localStorage, same idiom as the theme toggle) and
// stamped on <html data-ui> so the CSS skin applies before any screen paints.
// Everything re-renders through the 'opd:ui-lang' event — no reload needed.

const KEY = 'healther.opd.uiLang';
const EVENT = 'opd:ui-lang';

export function uiLang() {
  try { return window.localStorage.getItem(KEY) === 'v2' ? 'v2' : 'classic'; } catch (_) { return 'classic'; }
}

export function isUiV2() { return uiLang() === 'v2'; }

export function setUiLang(next) {
  const v = next === 'v2' ? 'v2' : 'classic';
  try { window.localStorage.setItem(KEY, v); } catch (_) {}
  stampUiLang();
  try { window.dispatchEvent(new CustomEvent(EVENT)); } catch (_) {}
}

// Stamp <html data-ui / data-accent / data-highlight / data-base> from the
// stored values. Called at module import (below) and after every set, so a hard
// reload lands styled from the first paint.
export function stampUiLang() {
  try {
    const el = document.documentElement;
    el.setAttribute('data-ui', uiLang());
    el.setAttribute('data-accent', uiAccent());
    el.setAttribute('data-highlight', uiHighlight());
    el.setAttribute('data-base', uiBase());
    applyCustomAccentVars(el);
  } catch (_) {}
}

/** React hook — current language, re-rendering on toggle. */
export function useUiLang() {
  const [lang, setLang] = React.useState(uiLang);
  React.useEffect(() => {
    const f = () => setLang(uiLang());
    window.addEventListener(EVENT, f);
    return () => window.removeEventListener(EVENT, f);
  }, []);
  return lang;
}

// ── v2 colour presets (owner 2026-08-03: "what if I wanted different accent
// colors? … there are two colors that we use, so we can change both"). Two
// independent families, device-local like the language itself:
//   accent    — the GO colour (buttons, active nav, ready states); default emerald
//   highlight — the ATTENTION colour (AI/proposed/overdue); default amber
// Clinical STOP (terracotta) is deliberately not configurable — red must mean
// stop for every user on every device. Values live in ui-v2.css under
// html[data-ui="v2"][data-accent=…]/[data-highlight=…].
// A THIRD family (owner 2026-08-04: "the highlight and accent don't change the
// base color. I should be able to change that base color too"):
//   base — the CANVAS: page background, card/popover surfaces, the muted +
//          border neutrals and the tone-bar/strip/panel tiers. It deliberately
//          does NOT touch --secondary/--accent (those belong to the accent
//          family) so the three rows stay independent.
// LIGHT THEME ONLY, by design: dark mode is a single deliberate ink-green
// palette, and re-tinting its near-black surfaces per preset would either read
// as a broken theme or need four more hand-tuned dark palettes. The avatar-menu
// row says so out loud while dark is active rather than silently ignoring the
// click (see shell-shared.jsx → ColorPresetRows).
const ACCENT_KEY = 'healther.opd.uiAccent';
const HIGHLIGHT_KEY = 'healther.opd.uiHighlight';
const BASE_KEY = 'healther.opd.uiBase';
export const UI_ACCENTS = [
  { id: 'emerald', label: 'Emerald', swatch: '#0b7d5c' },
  { id: 'blue', label: 'Blue', swatch: '#1d6fd8' },
  { id: 'violet', label: 'Violet', swatch: '#6d3fc4' },
  { id: 'rose', label: 'Rose', swatch: '#bd2f5e' },
  { id: 'ink', label: 'Black', swatch: '#20272a' },
  { id: 'slate', label: 'Gray', swatch: '#535f6e' },
  // Pastel/lighter family (owner ask, 2026-08-06). Each swatch is the exact
  // hex the corresponding html[data-ui="v2"]:not([data-theme="dark"])
  // [data-accent="…"] block in ui-v2.css renders as --primary — computed, not
  // eyeballed: DARK_INK_RGB (below) against the swatch clears WCAG AA
  // (>=4.5:1) for --primary-foreground text, same as every other preset.
  { id: 'sky', label: 'Pastel sky', swatch: '#b1d6f1' },
  { id: 'mint', label: 'Pastel mint', swatch: '#bce6d8' },
  { id: 'lavender', label: 'Pastel lavender', swatch: '#cebce6' },
  { id: 'blush', label: 'Pastel blush', swatch: '#eab8cd' },
  { id: 'sage', label: 'Pastel sage', swatch: '#cfdfc3' },
];
export const UI_HIGHLIGHTS = [
  { id: 'amber', label: 'Amber', swatch: '#c47b1f' },
  { id: 'orange', label: 'Orange', swatch: '#d9641a' },
  { id: 'sky', label: 'Sky', swatch: '#1487b8' },
];
// The swatch shows each preset's CANVAS colour (what actually changes); cards
// stay a tier lighter/whiter in every preset so a card never melts into it.
export const UI_BASES = [
  { id: 'bone', label: 'Bone', swatch: '#f7f8f6' },
  { id: 'white', label: 'Pure white', swatch: '#ffffff' },
  { id: 'cool', label: 'Cool gray', swatch: '#f1f3f6' },
  { id: 'sand', label: 'Warm sand', swatch: '#f7f3ea' },
];

// ── ONE custom-colour slot (owner ask, 2026-08-06) ──────────────────────────
// A device-picked hex, stored alongside the curated presets and applied
// through the exact same 7 CSS variables the ui-v2.css `[data-accent="…"]`
// blocks set — accent='custom' is the sentinel id, the hex lives in its own
// key. Same v2 + light-only scope as every other preset (see the block
// comment above); enforced in applyCustomAccentVars below, and re-checked on
// every theme toggle (the 'opd:theme' listener at the bottom of this file)
// since switching theme doesn't otherwise re-run stampUiLang().
//
// theme.js already has this exact hex→RGB→HSL/luminance math (hexToRgb,
// hexToHsl, luminance), but it is private to that file's IIFE and only
// exposes window.OPDTheme.setBrand — which deliberately targets the LEGACY
// --opd-* namespace only (owner decision, 2026-07-09: shadcn --primary/--ring
// stay monochrome by default; see theme.js's header comment). This file owns
// the shadcn --primary/--ring/--accent family instead, so the same small
// algorithm is duplicated here rather than widening theme.js's public surface
// for an unrelated namespace.
const CUSTOM_ACCENT_KEY = 'healther.opd.uiAccentCustomHex';
const CUSTOM_ACCENT_VARS = ['--primary', '--primary-foreground', '--ring', '--secondary', '--secondary-foreground', '--accent', '--accent-foreground'];
const MIN_CONTRAST = 4.5; // WCAG AA, normal text

export function hexToRgb(hex) {
  const h = String(hex || '').trim().replace(/^#/, '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
}
function rgbToHex([r, g, b]) {
  return '#' + [r, g, b].map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0')).join('');
}
function rgbToHsl([r, g, b]) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}
function hslToRgb(h, s, l) {
  h = ((h % 360) + 360) % 360; h /= 360; s /= 100; l /= 100;
  if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue2rgb = (t) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [Math.round(hue2rgb(h + 1 / 3) * 255), Math.round(hue2rgb(h) * 255), Math.round(hue2rgb(h - 1 / 3) * 255)];
}
function relLuminance([r, g, b]) {
  const lin = (c) => { const cs = c / 255; return cs <= 0.04045 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4); };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}
export function contrastRatio(rgbA, rgbB) {
  const a = relLuminance(rgbA), b = relLuminance(rgbB);
  const hi = Math.max(a, b), lo = Math.min(a, b);
  return (hi + 0.05) / (lo + 0.05);
}
function tripletOf(rgb) {
  const { h, s, l } = rgbToHsl(rgb);
  return `${Math.round(h)} ${Math.round(s * 10) / 10}% ${Math.round(l * 10) / 10}%`;
}
const WHITE_RGB = [255, 255, 255];
// Same ink as v2's own light --foreground (152 37.8% 8.8%) — reused so a
// custom accent's text reads as "part of the app" instead of plain black.
const DARK_INK_RGB = hslToRgb(152, 37.8, 8.8);

// Pick white or dark-ink foreground by contrast; if NEITHER clears 4.5:1 (only
// the narrow mid-lightness band can land there), darken the background in
// small steps — never lighten, so it stays recognisably "the colour picked" —
// until one candidate clears WCAG AA. Real values from a colour wheel rarely
// need more than a step or two; this loop is the clamp, not the common case.
function pickForegroundAndClampBg(rgb) {
  let cur = rgb;
  let cWhite = contrastRatio(cur, WHITE_RGB);
  let cDark = contrastRatio(cur, DARK_INK_RGB);
  if (Math.max(cWhite, cDark) < MIN_CONTRAST) {
    const { h, s, l } = rgbToHsl(cur);
    let lightness = l;
    for (let i = 0; i < 30 && Math.max(cWhite, cDark) < MIN_CONTRAST; i += 1) {
      lightness = Math.max(0, lightness - 3);
      cur = hslToRgb(h, s, lightness);
      cWhite = contrastRatio(cur, WHITE_RGB);
      cDark = contrastRatio(cur, DARK_INK_RGB);
    }
  }
  return { rgb: cur, foreground: cWhite >= cDark ? WHITE_RGB : DARK_INK_RGB };
}

/**
 * Derive the same 7 CSS custom properties a curated `[data-accent="…"]` block
 * sets in ui-v2.css, from an arbitrary hex — so a custom colour flows through
 * the identical variables/pipeline as a preset. Returns null on an invalid
 * hex. `appliedHex` is the (possibly contrast-clamped) primary colour, for
 * the picker UI to honestly reflect what's actually live.
 */
export function deriveCustomAccentVars(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const { rgb: primaryRgb, foreground } = pickForegroundAndClampBg(rgb);
  const { h, s } = rgbToHsl(primaryRgb);
  const ringRgb = hslToRgb(h, Math.min(85, s + 15), 45);
  const tintBgRgb = hslToRgb(h, Math.max(25, s - 15), 95);
  const tintFgDeep = hslToRgb(h, Math.min(85, s + 20), 30);
  const tintFgRgb = contrastRatio(tintFgDeep, tintBgRgb) >= MIN_CONTRAST
    ? tintFgDeep
    : (contrastRatio(WHITE_RGB, tintBgRgb) >= contrastRatio(DARK_INK_RGB, tintBgRgb) ? WHITE_RGB : DARK_INK_RGB);
  return {
    '--primary': tripletOf(primaryRgb),
    '--primary-foreground': tripletOf(foreground),
    '--ring': tripletOf(ringRgb),
    '--secondary': tripletOf(tintBgRgb),
    '--secondary-foreground': tripletOf(tintFgRgb),
    '--accent': tripletOf(tintBgRgb),
    '--accent-foreground': tripletOf(tintFgRgb),
    appliedHex: rgbToHex(primaryRgb),
  };
}

export function uiAccentCustomHex() {
  try {
    const v = window.localStorage.getItem(CUSTOM_ACCENT_KEY);
    return hexToRgb(v) ? v : null;
  } catch (_) { return null; }
}

export function setUiAccentCustom(hex) {
  if (!hexToRgb(hex)) return;
  try {
    window.localStorage.setItem(CUSTOM_ACCENT_KEY, hex);
    window.localStorage.setItem(ACCENT_KEY, 'custom');
  } catch (_) {}
  stampUiLang();
  try { window.dispatchEvent(new CustomEvent(EVENT)); } catch (_) {}
}

// Apply/clear the custom accent's inline vars on <html>, gated to the exact
// same v2 + light scope the static CSS attribute selectors use for every
// curated preset — an inline style otherwise wins the cascade unconditionally
// and would leak the custom colour into classic or dark. Cleared (not left
// stale) the moment that scope goes false.
function applyCustomAccentVars(el) {
  const inV2Light = el.getAttribute('data-ui') === 'v2' && el.getAttribute('data-theme') !== 'dark';
  const hex = inV2Light && uiAccent() === 'custom' ? uiAccentCustomHex() : null;
  const vars = hex ? deriveCustomAccentVars(hex) : null;
  CUSTOM_ACCENT_VARS.forEach((name) => {
    if (vars) el.style.setProperty(name, vars[name]);
    else el.style.removeProperty(name);
  });
}

export function uiAccent() {
  try {
    const v = window.localStorage.getItem(ACCENT_KEY);
    if (v === 'custom') return uiAccentCustomHex() ? 'custom' : 'emerald';
    return UI_ACCENTS.some((a) => a.id === v) ? v : 'emerald';
  } catch (_) { return 'emerald'; }
}
export function uiHighlight() {
  try {
    const v = window.localStorage.getItem(HIGHLIGHT_KEY);
    return UI_HIGHLIGHTS.some((a) => a.id === v) ? v : 'amber';
  } catch (_) { return 'amber'; }
}
export function setUiAccent(v) {
  try { window.localStorage.setItem(ACCENT_KEY, v); } catch (_) {}
  stampUiLang();
  try { window.dispatchEvent(new CustomEvent(EVENT)); } catch (_) {}
}
export function setUiHighlight(v) {
  try { window.localStorage.setItem(HIGHLIGHT_KEY, v); } catch (_) {}
  stampUiLang();
  try { window.dispatchEvent(new CustomEvent(EVENT)); } catch (_) {}
}
export function uiBase() {
  try {
    const v = window.localStorage.getItem(BASE_KEY);
    return UI_BASES.some((a) => a.id === v) ? v : 'bone';
  } catch (_) { return 'bone'; }
}
export function setUiBase(v) {
  try { window.localStorage.setItem(BASE_KEY, v); } catch (_) {}
  stampUiLang();
  try { window.dispatchEvent(new CustomEvent(EVENT)); } catch (_) {}
}

// Re-run the stamp (and therefore applyCustomAccentVars' scope check) on a
// theme toggle — dark/light switching doesn't otherwise touch this module, so
// without this a custom accent applied in light would keep leaking into dark
// until some other unrelated ui-lang/accent/base change happened to fire.
try { window.addEventListener('opd:theme', stampUiLang); } catch (_) {}

stampUiLang();
